// client/src/components/PhoneVerification.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import '../styles/components/PhoneVerification.css';

const API_URL = process.env.REACT_APP_API_URL;

// inline=true: 인라인 입력/버튼 표시(회원가입 호환), false: 모달 기능만 사용(아이디 찾기)
function PhoneVerification(
  { formData, setFormData, phoneVerified, setPhoneVerified, onVerified, inline = true },
  ref
) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [code, setCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => { setIsModalOpen(false); setMsg(''); setCode(''); };

  const normalize = (v='') => v.replace(/[^\d]/g, '').slice(0, 11);
  const valid = (v) => /^\d{10,11}$/.test(v);

  // phone / phoneNumber 모두 지원
  const getPhoneRaw = () => (formData?.phone ?? formData?.phoneNumber ?? '');
  const getPhoneNorm = () => normalize(getPhoneRaw());

  const handleChange = (e) => {
    const v = normalize(e.target.value);
    setFormData({ ...formData, phone: v, phoneNumber: v });
  };

  // 인증번호 요청
  const requestCode = async () => {
    openModal();
    const phone = getPhoneNorm();
    if (!valid(phone)) { setMsg('올바른 번호를 입력하세요.'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone })
      });
      const data = await res.json();
      if (res.ok) {
        setServerCode(String(data.code || '').trim());
        setMsg('');
        setCode('');
      } else {
        setMsg(data.message || '인증 요청 실패');
      }
    } catch {
      setMsg('서버 요청 실패');
    }
    setLoading(false);
  };

  // 인증번호 검증(서버 검증 우선, 실패/네트워크 에러 시 로컬 백업 비교)
  const verifyCode = async () => {
    const phone = getPhoneNorm();
    const input = String(code || '').trim();
    if (!valid(phone)) { setMsg('올바른 번호를 입력하세요.'); return; }
    if (!input) { setMsg('인증 코드를 입력하세요.'); return; }

    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/sms/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phone, code: input })
      });
      let data = {};
      try { data = await res.json(); } catch { data = {}; }

      const success =
        res.ok &&
        (data.ok === true ||
         data.verified === true ||
         data.success === true ||
         data.status === 'ok' ||
         data.result === 'ok');

      if (success) {
        setPhoneVerified(true);
      } else {
        if (input === String(serverCode || '').trim()) {
          setPhoneVerified(true);
        } else {
          setMsg(data.message || '인증 코드가 올바르지 않습니다.');
        }
      }
    } catch {
      if (input === String(serverCode || '').trim()) {
        setPhoneVerified(true);
      } else {
        setMsg('검증 실패');
      }
    }
    setVerifying(false);
  };

  // 인증 성공 시 모달 닫기 + 상위 알림(버튼 라벨/disable 반영)
  useEffect(() => {
    if (phoneVerified) {
      closeModal();
      if (typeof onVerified === 'function') onVerified();
    }
  }, [phoneVerified, onVerified]);

  useImperativeHandle(ref, () => ({
    open: () => openModal(),
    openAndRequest: async () => { await requestCode(); },
    request: async () => { await requestCode(); }
  }));

  return (
    <div>
      {inline && (
        <div className="phone-verification__input-group">
          <input
            className="phone-verification__input"
            name="phone"
            placeholder="휴대폰 번호"
            value={getPhoneRaw()}
            onChange={handleChange}
            disabled={phoneVerified || loading}
          />
          <button
            className="phone-verification__button"
            type="button"
            onClick={requestCode}
            disabled={loading || phoneVerified}
          >
            {phoneVerified ? '인증 완료' : (loading ? '요청 중...' : '휴대폰 인증 요청')}
          </button>
        </div>
      )}

      {isModalOpen && (
        <div className="phone-verification-modal">
          <div className="phone-verification-modal__content" onClick={(e) => e.stopPropagation()}>
            {valid(getPhoneNorm()) && (
              <>
                <p className="phone-verification-modal__message">인증번호가 문자로 전송되었습니다.</p>
                <p className="phone-verification-modal__desc">아래 칸에 인증번호를 입력해주세요.</p>
                <input
                  className="phone-verification-modal__input"
                  name="phoneCode"
                  placeholder="인증 코드 입력"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                <div className="phone-verification-modal__buttons">
                  <button
                    className="phone-verification-modal__button phone-verification-modal__button--primary"
                    type="button"
                    onClick={verifyCode}
                    disabled={verifying}
                  >
                    {verifying ? '확인 중...' : '인증 코드 확인'}
                  </button>
                  <button
                    className="phone-verification-modal__button"
                    type="button"
                    onClick={closeModal}
                  >
                    닫기
                  </button>
                </div>
              </>
            )}
            {msg && <p className="phone-verification-modal__error">{msg}</p>}
            {!valid(getPhoneNorm()) && (
              <div className="phone-verification-modal__buttons">
                <button
                  className="phone-verification-modal__button"
                  type="button"
                  onClick={closeModal}
                >
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(PhoneVerification);